import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, CheckSquare, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LABELS = { football_soccer: 'Football', tennis: 'Tennis', cricket: 'Cricket', esports: 'eSports' };

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const summarize = (run) => {
  if (run.status === 'running') return 'Running…';
  if (run.status === 'failed') return run.error || 'Failed';
  const r = run.result || {};
  if (run.kind === 'settle') {
    return `${r.markets?.resolved ?? 0} markets settled, ${r.markets?.pending ?? 0} waiting · ${r.picks?.graded ?? 0} picks graded`;
  }
  const created = ['football_soccer', 'tennis', 'cricket', 'esports'].map((k) => `${LABELS[k]} +${r[k]?.created ?? 0}`).join(', ');
  return `${created} · alert: ${r.picks_alert?.new_edges ?? 0} new edges to ${r.picks_alert?.users ?? 0} users`;
};

const RunRow = ({ run }) => (
  <li className="grid gap-1 py-3 border-b border-[#1e2a45] last:border-0 sm:grid-cols-[110px_90px_1fr_auto] sm:items-center" data-testid={`sports-run-${run.id}`}>
    <span className="text-sm text-white capitalize">{run.kind}</span>
    <span className="text-xs text-gray-500">{run.trigger}</span>
    <span className={`text-xs ${run.status === 'failed' ? 'text-red-400' : 'text-gray-300'}`} data-testid={`sports-run-summary-${run.id}`}>{summarize(run)}</span>
    <span className="text-xs text-gray-500">{new Date(run.started_at).toLocaleString()}{run.duration_s != null ? ` · ${run.duration_s}s` : ''}</span>
  </li>
);

export default function AdminSportsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const isAdmin = user?.email === 'oanjorin@anjolc.com' || user?.is_admin;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) navigate('/');
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/sports/admin/runs`, { headers: authHeaders(), credentials: 'include' });
    if (res.ok) setData(await res.json());
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const running = data?.runs?.some((r) => r.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async (kind) => {
    setBusy(kind);
    try {
      const res = await fetch(`${API_URL}/api/sports/${kind === 'sync' ? 'feeds/sync' : 'settle'}`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Request failed');
      if (body.started) toast.success(kind === 'sync' ? 'Sync started' : 'Settlement started');
      else toast.info(body.message);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="admin-sports-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <button type="button" onClick={() => navigate('/admin')} className="mb-6 flex items-center gap-1 text-sm text-gray-400 hover:text-white" data-testid="admin-sports-back">
          <ArrowLeft className="h-4 w-4" /> Admin panel
        </button>
        <h1 className="text-3xl font-bold text-white">Sports feeds</h1>
        <p className="mt-2 text-sm text-gray-400">Matches sync automatically every 3 hours and settle every 30 minutes. Use these buttons to run them now.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => start('sync')} disabled={!!busy || running} data-testid="admin-sports-sync-btn" className="bg-[#3B82F6] hover:bg-[#2563EB]">
            {busy === 'sync' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sync new matches
          </Button>
          <Button onClick={() => start('settle')} disabled={!!busy || running} variant="outline" data-testid="admin-sports-settle-btn" className="border-green-500/50 bg-transparent text-green-400 hover:bg-green-500/15 hover:text-green-300 focus-visible:text-green-300">
            {busy === 'settle' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckSquare className="h-4 w-4 mr-2" />}Settle finished matches
          </Button>
        </div>
        {data && (
          <>
            <div className="mt-8 grid gap-3 grid-cols-2 md:grid-cols-5" data-testid="admin-sports-counts">
              {Object.entries(data.open_markets).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[#1e2a45] bg-[#0d1424] p-4" data-testid={`admin-sports-count-${k}`}>
                  <div className="text-xs text-gray-500">{LABELS[k]} open</div><div className="text-2xl font-bold text-white">{v}</div>
                </div>
              ))}
              <div className="rounded-xl border border-[#1e2a45] bg-[#0d1424] p-4" data-testid="admin-sports-awaiting">
                <div className="text-xs text-gray-500">Awaiting result</div><div className="text-2xl font-bold text-amber-400">{data.awaiting_settlement}</div>
                {data.needs_admin > 0 && <div className="mt-1 text-xs text-red-400" data-testid="admin-sports-needs-admin">{data.needs_admin} need manual settling</div>}
              </div>
            </div>
            <h2 className="mt-10 mb-2 text-base md:text-lg font-semibold text-white">Latest runs</h2>
            {data.runs.length === 0
              ? <p className="text-sm text-gray-400" data-testid="admin-sports-no-runs">No runs yet.</p>
              : <ul className="rounded-xl border border-[#1e2a45] bg-[#0d1424] px-5" data-testid="admin-sports-runs">{data.runs.map((r) => <RunRow key={r.id} run={r} />)}</ul>}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
